import { useCallback, useEffect, useRef, useState } from 'react';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';

export interface IRateService {
	serviceId: string;
	service: string;
	price: number;
	coverage: string | null;
}

export interface ICountryRate {
	country: string;
	total: number;
	from: number;
	services: IRateService[];
}

export interface IRateSheet {
	marginPct: number;
	fetchedAt: string | null;
	source: string | null;
	vendor: string;
	countries: ICountryRate[];
}

/*
 * The rate card, fetched once per page load and shared by every KYC tile on the
 * canvas.
 *
 * A flow can carry several KYC steps and each one draws its own drawer, so a
 * per-component fetch would issue the same 45-country request once per node and
 * again on every open. The card changes when an ops script re-seeds it, not
 * between two clicks — pipelines.api caches it for a minute on its own side for
 * exactly the same reason.
 *
 * The in-flight PROMISE is cached, not just the result: two tiles mounting in
 * the same tick would otherwise both miss the empty cache and both fetch.
 */
let sheetCache: IRateSheet | null = null;
let inFlight: Promise<IRateSheet | null> | null = null;

export const useDataValidationRates = (enabled: boolean) => {
	const { get } = useNetwork({ updateState: false });
	const [sheet, setSheet] = useState<IRateSheet | null>(sheetCache);
	const [loading, setLoading] = useState(false);
	const mounted = useRef(true);

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	const load = useCallback(async () => {
		if (sheetCache) {
			setSheet(sheetCache);
			return;
		}
		setLoading(true);
		if (!inFlight) {
			inFlight = get(API_URL.DATA_VALIDATION_RATES)
				.then((resp: any) => {
					/* The service answers with a single-element array — one sheet per
					   (vendor, product) — so `[0]` is the sheet, not a first country. */
					const row = resp?.data?.[0] ?? null;
					if (!row || !Array.isArray(row.countries) || !row.countries.length) {
						return null;
					}
					sheetCache = row as IRateSheet;
					return sheetCache;
				})
				.catch(() => null)
				.finally(() => {
					inFlight = null;
				});
		}
		const resolved = await inFlight;
		if (!mounted.current) return;
		setSheet(resolved);
		setLoading(false);
		// useNetwork's `get` identity churns every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (enabled) load();
	}, [enabled, load]);

	return { sheet, loading };
};
